/**
 * La coquille commune : badge d'état, recherche d'adresse, message, onglets.
 *
 * Elle vit en dehors des vues, donc le champ de recherche ne perd jamais le
 * focus quand une page se redessine.
 */
import type { GeocodeResult } from '../core/types';
import { isRealFire, isWeakDetection } from '../core/zones';
import { geocode } from '../data/places';
import type { Actions } from '../state/actions';
import type { AppState } from '../state/store';
import { html, render } from './html';
import type { RouteName } from './router';

const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_MIN_CHARS = 3;

export interface Shell {
  render(state: AppState): void;
  setRoute(route: RouteName): void;
}

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Élément introuvable : ${selector}`);
  return element;
}

export function createShell(deps: { actions: Actions }): Shell {
  const status = required<HTMLElement>('#status');
  const input = required<HTMLInputElement>('#search');
  const results = required<HTMLElement>('#results');
  const messageBox = required<HTMLElement>('#message');
  const locateButton = required<HTMLButtonElement>('#locate');
  const tabs = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-route]'));

  let suggestions: GeocodeResult[] = [];
  let debounce: ReturnType<typeof setTimeout> | undefined;

  const hideResults = (): void => {
    results.hidden = true;
    suggestions = [];
  };

  input.addEventListener('input', () => {
    clearTimeout(debounce);
    const query = input.value.trim();
    if (query.length < SEARCH_MIN_CHARS) {
      hideResults();
      return;
    }
    debounce = setTimeout(async () => {
      try {
        suggestions = await geocode(query);
        if (suggestions.length === 0) {
          hideResults();
          return;
        }
        render(
          results,
          html`${suggestions.map(
            (result, index) => html`
              <button type="button" data-index="${String(index)}">
                ${result.label}<small>${result.context}</small>
              </button>
            `,
          )}`,
        );
        results.hidden = false;
      } catch {
        // Service de géocodage indisponible : on n'affiche rien plutôt qu'une
        // erreur, l'utilisateur peut réessayer en tapant.
        hideResults();
      }
    }, SEARCH_DEBOUNCE_MS);
  });

  results.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (!button) return;
    const chosen = suggestions[Number(button.dataset['index'])];
    if (!chosen) return;
    hideResults();
    input.value = '';
    input.blur();
    void deps.actions.setPosition(chosen.lat, chosen.lon, chosen.label);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target as HTMLElement).closest('.searchwrap')) hideResults();
  });

  // Échap ferme la liste : au clavier, il n'y avait aucun moyen d'en sortir.
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideResults();
  });

  locateButton.addEventListener('click', () => {
    void deps.actions.locateMe();
  });

  // Aucun écouteur sur les onglets : ce sont des liens, leur href suffit à
  // changer de vue, et le routeur écoute déjà hashchange.

  return {
    render(state) {
      /*
       * Ni les torcheres, ni les signaux faibles.
       *
       * Les compter donnait "63 feux en France" un jour sans incendie notable,
       * dont 29 detections a confiance basse degageant moins de 5 MW.
       */
      const count = state.zones.filter(
        (zone) => isRealFire(zone) && !isWeakDetection(zone),
      ).length;
      if (state.scanning && state.scanProgress) {
        status.textContent = `Recherche... ${state.scanProgress.done}/${state.scanProgress.total}`;
        status.dataset['hot'] = 'false';
      } else {
        status.textContent =
          count === 0
            ? 'Aucun feu détecté'
            : count === 1
              ? '1 feu en France'
              : `${count} feux en France`;
        status.dataset['hot'] = String(count > 0);
      }

      if (state.message) {
        render(messageBox, html`<p class="message">${state.message}</p>`);
      } else {
        messageBox.replaceChildren();
      }
    },

    setRoute(route) {
      for (const tab of tabs) {
        if (tab.dataset['route'] === route) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
      }
    },
  };
}
